import { useMutation } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import {
	exportDownloadHistoryFile,
	exportDownloadHistoryFiles,
} from '@api/downloadHistory';

import { fileSaver } from '@utils/fileSaver';

import { IGroupOperationWithActionDTO } from '@interfaces/common/defaultDTO';
import { IDownloadHistoryFilterDTO } from '@interfaces/downloadHistory/downloadHistoryDTO';

import { EMenuActionsTypes, ESnackbarTypes, TMenuActionsTypes } from '@enums/common';

import { allowedRolesDownloadHistory } from '@constants/downloadHistory';
import {
	DOWNLOAD_HISTORY_EXPORT_FILES,
} from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

interface IExportDownloadHistoryParams extends IDownloadHistoryFilterDTO {
}

interface IExportDownloadHistoryReturnType {
	exportFiles: (dto: IGroupOperationWithActionDTO) => void;
	isLoadingExportDownloadHistory: boolean;
}

export const useMutateExportDownloadHistory = (
	params: IExportDownloadHistoryParams,
): IExportDownloadHistoryReturnType => {
	const dispatch = useAppDispatch();

	const {
		source,
		status,
		searchValue,
	} = params || {};

	const {
		mutate: exportFiles,
		isLoading: isLoadingExportFiles,
	} = useMutation<IGroupOperationWithActionDTO>({
		mutationKey: [DOWNLOAD_HISTORY_EXPORT_FILES],
		allowedRoles: allowedRolesDownloadHistory,
		mutationFn: (data: { ids: string[]; actionType: TMenuActionsTypes }) => {
			const oneFile = data.ids.length === 1;

			if (oneFile) {
				exportDownloadHistoryFile({ id: data.ids[0] })
					.then((data) => {
					if (data?.downloadUrl) fileSaver(data.downloadUrl);
				});
			} else {
				const filter: IDownloadHistoryFilterDTO = {
					source,
					status,
					searchValue,
				};

				exportDownloadHistoryFiles({
					filter: (data.actionType === EMenuActionsTypes.allFiltered) && filter
						? filter
						: null,
					ids: data.ids,
					isAll: data.actionType === EMenuActionsTypes.all
						|| data.actionType === EMenuActionsTypes.allFiltered,
				}).then((data) => {
					if (data?.downloadUrl) fileSaver(data.downloadUrl);
				});
			}
		},
		onSuccess: (_, variables) => {
			dispatch(setSnackbar({
				message: variables.actionType
					? responseMessages.exportDownloadHistoryFilesStarted
					: responseMessages.exportDownloadHistoryFileStarted,
				type: ESnackbarTypes.success,
			}));
		},
	});

	return {
		exportFiles,
		isLoadingExportDownloadHistory: isLoadingExportFiles,
	};
};
